import "core-js/modules/es6.object.assign";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.string.iterator";
import "core-js/modules/es6.map";
import { useMemo, useReducer, useCallback, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import Axios from 'axios';
import buildURL from 'axios/lib/helpers/buildURL';
var Cache = new Map();
var CacheRequests = new Map();

function init(_a) {
  var config = _a.config,
      cacheKey = _a.cacheKey,
      data = _a.data,
      prevConfig = _a.prevConfig;
  var hasCache = typeof cacheKey === 'string' && Cache.has(cacheKey);
  return {
    data: cacheKey && hasCache ? Cache.get(cacheKey) : data,
    isFetching: config != null && !hasCache,
    config: config,
    error: null,
    requestId: 1,
    prevConfig: prevConfig
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'manually set config':
      {
        var _a = action.payload,
            config = _a.config,
            cacheKey = _a.cacheKey;

        if (config === state.config) {
          return state;
        }

        return init({
          config: config,
          cacheKey: cacheKey,
          prevConfig: state.prevConfig,
          data: state.data
        });
      }

    case 'config changed':
      {
        var _b = action.payload,
            config = _b.config,
            cacheKey = _b.cacheKey;

        if (config === state.prevConfig) {
          return state;
        }

        return init({
          config: config,
          cacheKey: cacheKey,
          prevConfig: config,
          data: state.data
        });
      }

    case 'poll':
      return {
        config: state.config,
        data: state.data,
        prevConfig: state.prevConfig,
        isFetching: true,
        error: null,
        requestId: state.requestId + 1
      };

    case 'fetched':
      return {
        config: state.config,
        prevConfig: state.prevConfig,
        requestId: state.requestId,
        data: action.payload,
        isFetching: false,
        error: null
      };

    case 'error':
      return {
        config: state.config,
        prevConfig: state.prevConfig,
        requestId: state.requestId,
        data: state.data,
        isFetching: false,
        error: action.payload
      };

    default:
      return state;
  }
}

function useAxiosRequest(axiosConfig, _a) {
  var _b = _a === void 0 ? {} : _a,
      _c = _b.cache,
      cache = _c === void 0 ? false : _c,
      _d = _b.pollInterval,
      pollInterval = _d === void 0 ? 0 : _d,
      onSuccess = _b.onSuccess,
      onError = _b.onError;

  var cacheKey = useMemo(function () {
    if (axiosConfig && cache) {
      return typeof axiosConfig === 'string' ? axiosConfig : buildURL(axiosConfig.url, axiosConfig.params);
    }

    return null;
  }, [axiosConfig, cache]);
  var initialValue = {
    config: axiosConfig,
    cacheKey: cacheKey,
    data: null,
    prevConfig: axiosConfig
  };

  var _e = useReducer(reducer, initialValue, // FIX ME
  init),
      state = _e[0],
      dispatch = _e[1];

  var updateConfig = useCallback(function (config) {
    dispatch({
      type: 'config changed',
      payload: {
        config: config,
        cacheKey: cacheKey
      }
    });
  }, [dispatch, cacheKey]);
  var dispatchManullySetConfig = useCallback(function (config) {
    dispatch({
      type: 'manually set config',
      payload: {
        config: config,
        cacheKey: cacheKey
      }
    });
  }, [dispatch, cacheKey]);
  var dispatchFetched = useCallback(function (data) {
    dispatch({
      type: 'fetched',
      payload: data
    });
  }, [dispatch]);
  var dispatchError = useCallback(function (error) {
    dispatch({
      type: 'error',
      payload: error
    });
  }, [dispatch]);
  var dispatchPoll = useCallback(function () {
    dispatch({
      type: 'poll'
    });
  }, [dispatch]);
  var cb = useCallback(function (data) {
    if (typeof onSuccess === 'function') {
      unstable_batchedUpdates(function () {
        dispatchFetched(data);
        onSuccess(data);
      });
    } else {
      dispatchFetched(data);
    }
  }, [dispatchFetched, onSuccess]);
  var errorCb = useCallback(function (error) {
    if (typeof onError === 'function') {
      unstable_batchedUpdates(function () {
        dispatchError(error);
        onError(error);
      });
    } else {
      dispatchError(error);
    }
  }, [dispatchError, onError]);

  if (state.prevConfig !== axiosConfig) {
    updateConfig(axiosConfig);
  }

  useEffect(function () {
    if (pollInterval > 0 && !state.isFetching) {
      var timeoutId_1 = setTimeout(dispatchPoll, pollInterval);
      return function () {
        clearTimeout(timeoutId_1);
      };
    }
  }, [state.isFetching, pollInterval, dispatchPoll]);
  useEffect(function () {
    if (state.config && state.isFetching) {
      var source = Axios.CancelToken.source();
      var axiosConfig_1 = {
        cancelToken: source.token
      };

      if (typeof state.config === 'string') {
        axiosConfig_1.url = state.config;
        axiosConfig_1.method = 'GET';
      } else {
        Object.assign(axiosConfig_1, state.config);
      }

      var request = void 0;

      if (cacheKey) {
        var maybeRequest = CacheRequests.get(cacheKey);

        if (maybeRequest) {
          request = maybeRequest;
        } else {
          request = Axios(axiosConfig_1);
          CacheRequests.set(cacheKey, request);
        }
      } else {
        request = Axios(axiosConfig_1);
      }

      request.then(function (_a) {
        var data = _a.data;
        cb(data);

        if (cacheKey) {
          Cache.set(cacheKey, data);
        }
      })["catch"](function (error) {
        if (Axios.isCancel(error)) {
          return;
        }

        errorCb(error);
      }).then(function () {
        if (cacheKey) {
          CacheRequests["delete"](cacheKey);
        }
      });
      return source.cancel;
    }
  }, [state.config, state.isFetching, cb, errorCb, state.requestId, cacheKey]);
  return {
    isFetching: state.isFetching,
    requestId: state.requestId,
    data: state.data,
    error: state.error,
    refresh: dispatchPoll,
    update: dispatchManullySetConfig,
    config: state.config
  };
}

function useAxiosRequestRender(_a) {
  var config = _a.config,
      options = _a.options,
      render = _a.render,
      renderLoading = _a.renderLoading,
      renderError = _a.renderError;
  var axiosRequest = useAxiosRequest(config, options);

  if (axiosRequest.isFetching && axiosRequest.requestId === 1) {
    return renderLoading(axiosRequest);
  }

  if (axiosRequest.error) {
    return renderError(axiosRequest);
  }

  return render(axiosRequest);
}

export { Cache, CacheRequests, useAxiosRequest, useAxiosRequestRender };